import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { API } from "@/App";
import {
  ShoppingCart,
  ArrowLeft,
  Trash2,
  FileText,
  BookOpen,
  IndianRupee,
  Loader2,
  CheckCircle,
  Mail,
  User,
  ShieldCheck,
  CreditCard,
  Gift,
} from "lucide-react";

export default function Checkout() {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, clearCart, getCartTotal, getCartCount } = useCart();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [checkoutComplete, setCheckoutComplete] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  
  // Guest checkout form
  const [guestEmail, setGuestEmail] = useState("");
  const [guestName, setGuestName] = useState("");
  const [useGuestCheckout, setUseGuestCheckout] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      // Verify token and get user email
      fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.email) {
            setIsAuthenticated(true);
            setUserEmail(data.email);
          }
        })
        .catch(() => setIsAuthenticated(false));
    }
  }, []);

  const subtotal = getCartTotal();
  const gst = Math.round(subtotal * 0.18);
  const total = subtotal + gst;

  const handleGuestCheckout = async () => {
    if (!guestEmail) {
      toast.error("Please enter your email address");
      return;
    }
    
    if (!guestEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API}/shop/guest-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_ids: cartItems.map((item) => item.id),
          email: guestEmail,
          name: guestName || null,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setCheckoutComplete(true);
        setOrderDetails(data);
        clearCart();
        toast.success("Order complete! Check your email for download links.");
      } else {
        toast.error(data.detail || "Checkout failed. Please try again.");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Something went wrong. Please try again.");
    }
    setIsLoading(false);
  };

  const handleAuthenticatedCheckout = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please login to continue");
      navigate("/login?redirect=/checkout");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API}/shop/purchase/bulk`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          product_ids: cartItems.map((item) => item.id),
          payment_method: "demo", // For now, demo payment
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Mark orders as completed (demo mode)
        await fetch(`${API}/shop/demo-complete/${data.bulk_order_id}`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => {}); // Ignore if endpoint doesn't exist

        setCheckoutComplete(true);
        setOrderDetails(data);
        clearCart();
        toast.success("Order complete! Check My Purchases to download.");
      } else {
        toast.error(data.detail || "Checkout failed. Please try again.");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Something went wrong. Please try again.");
    }
    setIsLoading(false);
  };

  // Order Complete Screen
  if (checkoutComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-6">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Order Complete!</h1>
            <p className="text-gray-600 mb-6">
              {useGuestCheckout
                ? `Download links have been sent to ${guestEmail}`
                : "Your purchases are ready to download"}
            </p>

            {orderDetails && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-gray-600">
                  <strong>Order ID:</strong> {orderDetails.guest_order_id || orderDetails.bulk_order_id}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Items:</strong> {orderDetails.items_count}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Total:</strong> ₹{orderDetails.total_amount}
                </p>
              </div>
            )}

            <div className="space-y-3">
              {!useGuestCheckout && (
                <Button
                  onClick={() => navigate("/purchases")}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  View My Purchases
                </Button>
              )}
              <Button
                onClick={() => navigate("/eduquest")}
                variant="outline"
                className="w-full"
              >
                Continue Shopping
              </Button>
            </div>

            {useGuestCheckout && (
              <p className="text-xs text-gray-500 mt-4">
                Create an account to access your purchases anytime!
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Empty Cart
  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-6">
            <ShoppingCart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Your cart is empty</h2>
            <p className="text-gray-600 mb-6">Add some worksheets or question banks to get started</p>
            <Button onClick={() => navigate("/eduquest")} className="bg-emerald-600 hover:bg-emerald-700">
              Browse Learning Shop
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate("/eduquest")} className="text-gray-600">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Shop
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
          <ShoppingCart className="w-6 h-6" />
          Checkout
        </h1>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <CardHeader className="py-4">
                <CardTitle className="text-lg">Your Items ({getCartCount()})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-gray-100">
                  {cartItems.map((item) => (
                    <div key={item.id} className="flex items-center gap-4 p-4">
                      <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          item.product_type === "worksheet" ? "bg-blue-100" : "bg-purple-100"
                        }`}
                      >
                        {item.product_type === "worksheet" ? (
                          <FileText className="w-6 h-6 text-blue-600" />
                        ) : (
                          <BookOpen className="w-6 h-6 text-purple-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 truncate">{item.topic_display}</p>
                        <p className="text-sm text-gray-500">
                          {item.product_type === "worksheet" ? "Worksheet" : "Question Bank"} •
                          Class {item.grade} • {item.subject_display}
                        </p>
                      </div>
                      <span
                        className={`font-bold flex items-center ${
                          item.product_type === "worksheet" ? "text-blue-600" : "text-purple-600"
                        }`}
                      >
                        <IndianRupee className="w-3 h-3" />
                        {item.price}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFromCart(item.id)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Checkout Options */}
            <Card>
              <CardHeader className="py-4">
                <CardTitle className="text-lg">Checkout Options</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isAuthenticated ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="font-medium text-emerald-800">Logged in as {userEmail}</p>
                        <p className="text-sm text-emerald-600">
                          Your purchases will be saved to your account
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Option to Login */}
                    <button
                      onClick={() => navigate("/login?redirect=/checkout")}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        !useGuestCheckout
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <User className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">Login to Purchase</p>
                          <p className="text-sm text-gray-500">Save purchases to your account</p>
                        </div>
                      </div>
                    </button>

                    {/* Guest Checkout Option */}
                    <button
                      onClick={() => setUseGuestCheckout(true)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        useGuestCheckout
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                          <Mail className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">Guest Checkout</p>
                          <p className="text-sm text-gray-500">Get download links via email</p>
                        </div>
                      </div>
                    </button>

                    {/* Guest Email Form */}
                    {useGuestCheckout && (
                      <div className="space-y-3 pt-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email Address *
                          </label>
                          <Input
                            type="email"
                            placeholder="your@email.com"
                            value={guestEmail}
                            onChange={(e) => setGuestEmail(e.target.value)}
                            className="w-full"
                            data-testid="guest-email-input"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Name (optional)
                          </label>
                          <Input
                            type="text"
                            placeholder="Your name"
                            value={guestName}
                            onChange={(e) => setGuestName(e.target.value)}
                            className="w-full"
                            data-testid="guest-name-input"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader className="py-4 bg-gray-50 border-b">
                <CardTitle className="text-lg">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-3">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal ({getCartCount()} items)</span>
                    <span className="flex items-center">
                      <IndianRupee className="w-3 h-3" />
                      {subtotal}
                    </span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>GST (18%)</span>
                    <span className="flex items-center">
                      <IndianRupee className="w-3 h-3" />
                      {gst}
                    </span>
                  </div>
                  <div className="border-t pt-3 flex justify-between font-bold text-lg">
                    <span>Total</span>
                    <span className="text-emerald-600 flex items-center">
                      <IndianRupee className="w-4 h-4" />
                      {total}
                    </span>
                  </div>
                </div>

                <Button
                  onClick={isAuthenticated ? handleAuthenticatedCheckout : handleGuestCheckout}
                  disabled={isLoading || (!isAuthenticated && !useGuestCheckout)}
                  className="w-full mt-6 bg-emerald-600 hover:bg-emerald-700 text-white py-6"
                  data-testid="complete-purchase-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Complete Purchase
                    </>
                  )}
                </Button>

                <p className="text-xs text-gray-400 text-center mt-4">
                  By completing this purchase, you agree to our Terms of Service
                </p>

                {/* Demo Notice */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
                  <p className="text-xs text-yellow-800 flex items-start gap-2">
                    <Gift className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong>Demo Mode:</strong> Payment is simulated. Downloads will be
                      available immediately after checkout.
                    </span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
